import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Loader2, CreditCard, ArrowUpCircle, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { extractApiErrorMessage, networkErrorMessage } from '../lib/errors';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [upgradingPlan, setUpgradingPlan] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState('');
  const trialBlocked = !!user?.trial_blocked;
  const expiredReason = user?.trial?.expired_reason;
  const paymentBlocked = !!user?.payment_blocked;
  const payment = user?.payment || {};

  const fetchSubscription = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/stripe/subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSubscription(data);
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao carregar assinatura'));
      }
    } catch (error) {
      toast.error(networkErrorMessage(error, 'Não foi possível carregar sua assinatura.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms`);
      const data = await res.json();
      const allPlans = data?.pricing?.plans || [];
      // Only plans with stripe_price_id can be upgraded to
      setPlans(allPlans.filter((p) => p.stripe_price_id));
    } catch (error) {
      // ignore
    }
  }, []);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('upgrade_session_id');
    const cancelled = params.get('upgrade_cancel');

    if (cancelled) {
      toast.info('Upgrade cancelado.');
      navigate('/subscription', { replace: true });
      return;
    }

    if (sessionId) {
      setFinalizing(true);
      const token = localStorage.getItem('token');
      fetch(`${API_URL}/api/stripe/upgrade/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId })
      })
        .then(async (r) => {
          if (r.ok) {
            const data = await r.json();
            if (data.success) {
              toast.success(`Upgrade concluído! Plano: ${data.plan}`);
              // Refresh user so trial_blocked / plan state is updated immediately
              // (no more "log out / log in" required after upgrading).
              await refreshUser();
            } else {
              toast.error('Não foi possível finalizar o upgrade. Verifique no Stripe.');
            }
          } else {
            toast.error(await extractApiErrorMessage(r, 'Falha ao finalizar upgrade'));
          }
        })
        .catch((err) => toast.error(networkErrorMessage(err, 'Não foi possível finalizar o upgrade.')))
        .finally(() => {
          setFinalizing(false);
          navigate('/subscription', { replace: true });
          fetchSubscription();
        });
    }
  }, [location.search, navigate, fetchSubscription, refreshUser]);

  useEffect(() => {
    fetchSubscription();
    fetchPlans();
  }, [fetchSubscription, fetchPlans]);

  const handleOpenPortal = async () => {
    setOpeningPortal(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/stripe/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ host_url: window.location.origin })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
          return;
        }
        toast.error('Não foi possível abrir o portal. Tente novamente.');
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao abrir portal'));
      }
    } catch (error) {
      toast.error(networkErrorMessage(error, 'Não foi possível abrir o portal de assinatura.'));
    } finally {
      setOpeningPortal(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/stripe/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Assinatura cancelada.');
        setCancelOpen(false);
        setCancelConfirm('');
        await refreshUser();
        fetchSubscription();
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao cancelar assinatura'));
      }
    } catch (error) {
      toast.error(networkErrorMessage(error, 'Não foi possível cancelar a assinatura.'));
    } finally {
      setCanceling(false);
    }
  };

  const handleUpgrade = async (planName) => {
    setUpgradingPlan(planName);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/stripe/upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ plan_id: planName, host_url: window.location.origin })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
          return;
        }
        toast.error('Não foi possível iniciar o upgrade. Tente novamente.');
        setUpgradingPlan(null);
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao iniciar upgrade'));
        setUpgradingPlan(null);
      }
    } catch (error) {
      toast.error(networkErrorMessage(error, 'Não foi possível conectar ao Stripe.'));
      setUpgradingPlan(null);
    }
  };

  if (loading || finalizing) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin" />
        {finalizing && <span className="ml-3 text-sm text-muted-foreground">Finalizando upgrade...</span>}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Este aviso fala do que VAI acontecer no dia 11, então não pode aparecer para quem já
          passou por lá: no estado bloqueado a assinatura já foi cancelada, e a faixa vermelha
          do topo (TrialGuard) somada aos planos abaixo já dizem o que fazer. */}
      {paymentBlocked && payment.phase !== 'locked_out' && (() => {
        const isModerate = payment.phase === 'moderate';
        const tone = isModerate
          ? { border: 'border-orange-300', bg: 'bg-orange-50', iconBg: 'bg-orange-200', iconColor: 'text-orange-700', title: 'text-orange-900', body: 'text-orange-800', sub: 'text-orange-700' }
          : { border: 'border-red-300',    bg: 'bg-red-50',    iconBg: 'bg-red-200',    iconColor: 'text-red-700',    title: 'text-red-900',    body: 'text-red-800',    sub: 'text-red-700' };
        return (
          <div
            className={`rounded-lg border-2 ${tone.border} ${tone.bg} p-5 flex items-start gap-4`}
            data-testid="payment-blocked-banner"
          >
            <div className={`p-2 rounded-full ${tone.iconBg} flex-shrink-0`}>
              <CreditCard className={`w-5 h-5 ${tone.iconColor}`} />
            </div>
            <div className="flex-1">
              <h3 className={`font-bold ${tone.title} text-lg mb-1`}>
                {payment.phase === 'hard'
                  ? 'Acesso restrito por pagamento pendente'
                  : 'Seu pagamento está pendente'}
              </h3>
              <p className={`text-sm ${tone.body}`}>
                A cobrança da renovação da sua assinatura falhou
                {payment.days_past_due ? <> há <strong>{payment.days_past_due} dias</strong></> : null}.
                {payment.phase === 'hard' ? (
                  <> O acesso à plataforma está limitado a <strong>Minha Assinatura</strong> e <strong>Meus Dados</strong> até a regularização.</>
                ) : (
                  <> O envio de mensagens e a criação de campanhas estão temporariamente bloqueados.</>
                )}
                {' '}Clique em <strong>Gerenciar Plano</strong> abaixo para atualizar seu cartão.
              </p>
              <p className={`text-xs ${tone.sub} mt-2`}>
                Se o pagamento não for regularizado em até <strong>11 dias</strong> desde a falha,
                sua assinatura será <strong>cancelada</strong> e o acesso ao sistema bloqueado.
                Não haverá cobrança retroativa nem novas cobranças, e seus dados continuarão
                salvos: para voltar a usar, basta fazer uma nova assinatura.
              </p>
            </div>
          </div>
        );
      })()}
      {trialBlocked && (
        <div
          className="rounded-lg border-2 border-red-300 bg-red-50 p-5 flex items-start gap-4"
          data-testid="trial-expired-banner"
        >
          <div className="p-2 rounded-full bg-red-200 flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-red-900 text-lg mb-1">
              Seu período de teste acabou
            </h3>
            <p className="text-sm text-red-800">
              {expiredReason === 'limit'
                ? 'Você atingiu o limite de mensagens do plano Trial. '
                : expiredReason === 'time_and_limit'
                ? 'Seu trial expirou no tempo e no limite de mensagens. '
                : 'O período gratuito de avaliação chegou ao fim. '}
              Para continuar enviando mensagens, gerenciando contatos e usando todas as
              funcionalidades, <strong>escolha um plano abaixo</strong>.
            </p>
            <p className="text-xs text-red-700 mt-2">
              Os seus dados (contatos, mensagens, campanhas) estão preservados e estarão
              disponíveis assim que você assinar um plano.
            </p>
          </div>
        </div>
      )}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
              <CreditCard className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </div>
            <div>
              <CardTitle>Gerenciar Meu Plano</CardTitle>
              <CardDescription>
                Gerencie seu plano e dados de pagamento.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {!user?.subscription_canceled && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 border rounded-lg" data-testid="subscription-plan-card">
                <p className="text-sm text-muted-foreground font-semibold">Plano Atual</p>
                <p className="text-xl font-bold mt-1">{subscription?.plan || 'Não definido'}</p>
              </div>
              <div className="p-4 border rounded-lg" data-testid="subscription-limit-card">
                <p className="text-sm text-muted-foreground font-semibold">Limite Mensal</p>
                <p className="text-xl font-bold mt-1">
                  {subscription?.monthly_limit === 0
                    ? 'Ilimitadas'
                    : `${subscription?.monthly_limit?.toLocaleString() || '—'} msgs`}
                </p>
              </div>
              <div className="p-4 border rounded-lg" data-testid="subscription-status-card">
                <p className="text-sm text-muted-foreground font-semibold">Status</p>
                <Badge className="mt-2 bg-teal-100 text-teal-800">
                  Ativo
                </Badge>
              </div>
            </div>
          )}

          {subscription?.has_stripe ? (
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-3 font-semibold">
                Gerencie ou altere seu plano, cartão de crédito e faturas.
              </p>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Button
                  onClick={handleOpenPortal}
                  disabled={openingPortal}
                  data-testid="subscription-manage-button"
                  className="btn-shine-wrapper bg-teal-500 hover:bg-teal-600 text-white"
                >
                  {openingPortal && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <span>Gerenciar Plano</span>
                </Button>
                {user?.is_tenant_owner && (
                  <Button
                    variant="outline"
                    onClick={() => setCancelOpen(true)}
                    data-testid="subscription-cancel-button"
                    className="ml-auto text-muted-foreground border-muted-foreground/30 hover:text-red-700 hover:border-red-300 hover:bg-red-50"
                  >
                    Cancelar assinatura
                  </Button>
                )}
              </div>

              <Dialog
                open={cancelOpen}
                onOpenChange={(o) => {
                  if (canceling) return;
                  setCancelOpen(o);
                  if (!o) setCancelConfirm('');
                }}
              >
                <DialogContent className="sm:max-w-xl" data-testid="cancel-subscription-modal">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-xl text-red-700">
                      <AlertTriangle className="w-5 h-5" />
                      Cancelar assinatura
                    </DialogTitle>
                  </DialogHeader>
                  <p className="text-base text-foreground/80">
                    Sua assinatura será cancelada <strong>imediatamente</strong>. Você perderá o
                    acesso a <strong>enviar mensagens</strong>, <strong>criar campanhas</strong> e
                    <strong> conectar o WhatsApp</strong>. Seus contatos, grupos e histórico
                    continuam salvos, e você pode reativar um plano quando quiser.
                  </p>
                  <div className="space-y-2 pt-1">
                    <Label htmlFor="cancel-confirm" className="text-base">
                      Para confirmar, digite <strong>CANCELAR</strong> abaixo:
                    </Label>
                    <Input
                      id="cancel-confirm"
                      value={cancelConfirm}
                      onChange={(e) => setCancelConfirm(e.target.value.toUpperCase())}
                      placeholder="CANCELAR"
                      autoComplete="off"
                      disabled={canceling}
                      data-testid="cancel-confirm-input"
                      className="uppercase placeholder:normal-case"
                    />
                  </div>
                  <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                      variant="outline"
                      onClick={() => { setCancelOpen(false); setCancelConfirm(''); }}
                      disabled={canceling}
                      data-testid="cancel-subscription-back"
                    >
                      Voltar
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleCancelSubscription}
                      disabled={canceling || cancelConfirm.trim().toUpperCase() !== 'CANCELAR'}
                      data-testid="cancel-subscription-confirm"
                    >
                      {canceling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Cancelar assinatura
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          ) : (
            <div className="pt-4 border-t space-y-4">
              <div>
                <h3 className="font-semibold text-base mb-1">Fazer Upgrade</h3>
                <p className="text-sm text-muted-foreground">
                  Escolha um plano abaixo para ativar uma assinatura. Após o pagamento, seu plano será atualizado automaticamente.
                </p>
              </div>
              {plans.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhum plano de upgrade disponível no momento. Entre em contato com o suporte.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {plans.map((p) => (
                    <div
                      key={p.name}
                      className="border rounded-lg p-4 flex flex-col"
                      data-testid={`upgrade-plan-${p.name}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-lg">{p.name}</h4>
                        {!user?.subscription_canceled && subscription?.plan === p.name && (
                          <Badge className="text-xs bg-teal-100 text-teal-800">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Atual
                          </Badge>
                        )}
                      </div>
                      <p className="text-2xl font-bold">
                        R$ {p.price_monthly}
                        <span className="text-sm font-normal text-muted-foreground">/mês</span>
                      </p>
                      {p.features && p.features.length > 0 && (
                        <ul className="mt-3 space-y-1 text-sm text-muted-foreground flex-1">
                          {p.features.slice(0, 4).map((f, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                              <span>{typeof f === 'string' ? f : f.text}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <Button
                        className="w-full mt-4 text-[15px]"
                        onClick={() => handleUpgrade(p.name)}
                        disabled={!!upgradingPlan}
                        data-testid={`upgrade-button-${p.name}`}
                      >
                        {upgradingPlan === p.name ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <ArrowUpCircle className="w-4 h-4 mr-2" />
                        )}
                        Fazer Upgrade
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
